import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { addDays } from 'date-fns';
import { htmlToText } from './misc.utils';
import { ValidationErrorValues } from './validation.util';

export const minLengthArray = (min: number): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: unknown } | null => {
    if (control.value.length >= min) return null;

    return { minLengthArray: { min, actual: control.value.length } };
  };
};

export const requiredLength = (requiredLength: number): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: unknown } | null => {
    if (typeof control.value === 'string' && control.value.length === requiredLength) return null;
    return { requiredLength: { requiredLength, actual: control.value?.length ?? 0 } };
  };
};
export const greaterThan = (field: string, fieldName?: string): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: unknown } | null => {
    const group = control.parent;
    if (!group) return null;
    const fieldToCompare = group.get(field);
    if (!fieldToCompare) return null;
    if (control.value !== 0 && !control.value) {
      fieldToCompare.updateValueAndValidity();
      return null;
    }
    const isLessThan = fieldToCompare.value && Number(control.value) <= Number(fieldToCompare.value);
    fieldToCompare.updateValueAndValidity();
    return isLessThan ? { lessThan: { min: fieldToCompare.value, actual: control.value, fieldName } } : null;
  };
};
export const greaterThanOrEquals = (field: string, fieldName?: string): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: unknown } | null => {
    const group = control.parent;
    if (!group) return null;
    const fieldToCompare = group.get(field);
    if (!fieldToCompare) return null;
    if (control.value !== 0 && !control.value) {
      fieldToCompare.updateValueAndValidity();
      return null;
    }
    const isLessThan = fieldToCompare.value && Number(control.value) < Number(fieldToCompare.value);
    fieldToCompare.updateValueAndValidity();
    return isLessThan ? { lessThan: { min: fieldToCompare.value, actual: control.value, fieldName } } : null;
  };
};

export const lessThan = (field: string, fieldName?: string): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: unknown } | null => {
    const group = control.parent;
    if (!group) return null;
    const fieldToCompare = group.get(field);
    if (!fieldToCompare) return null;
    if (fieldToCompare.value !== 0 && !fieldToCompare.value) return null;
    const isGreaterThan = fieldToCompare.value && Number(fieldToCompare.value) <= Number(control.value);
    return isGreaterThan ? { greaterThan: { min: fieldToCompare.value, actual: control.value, fieldName } } : null;
  };
};
export const lessThanOrEquals = (field: string, fieldName?: string): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: unknown } | null => {
    const group = control.parent;
    if (!group) return null;
    const fieldToCompare = group.get(field);
    if (!fieldToCompare) return null;
    if (fieldToCompare.value !== 0 && !fieldToCompare.value) return null;
    const isGreaterThan = fieldToCompare.value && Number(fieldToCompare.value) < Number(control.value);
    return isGreaterThan ? { greaterThan: { min: fieldToCompare.value, actual: control.value, fieldName } } : null;
  };
};

export const notSimilar = (field: string, errorMessage?: string): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: unknown } | null => {
    const group = control.parent;
    if (!group) return null;
    const fieldToCompare = group.get(field);
    if (!fieldToCompare) return null;
    const isSame = fieldToCompare.value === control.value;
    return isSame ? { same: errorMessage ?? { compare: fieldToCompare.value, actual: control.value } } : null;
  };
};

export const noMatch = (field: string, errorMessage?: string): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: unknown } | null => {
    const group = control.parent;
    if (!group) return null;
    const fieldToCompare = group.get(field);
    if (!fieldToCompare) return null;
    const isSame = fieldToCompare.value === control.value;
    return isSame ? null : { match: errorMessage ?? { compare: fieldToCompare.value, actual: control.value } };
  };
};

export const requiredIf = (controllerOrField: AbstractControl | string, value: unknown): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: unknown } | null => {
    let toCompareController: AbstractControl | null;

    if (typeof controllerOrField === 'string') {
      const group = control.parent;
      if (!group) return null;
      toCompareController = group.get(controllerOrField);
    } else {
      toCompareController = controllerOrField;
    }
    if (!toCompareController) return null;

    if (!toCompareController) return null;
    const isRequired =
      typeof value === 'function' ? value(toCompareController.value) : toCompareController.value === value;

    if (isRequired && control.value === '00000000-0000-0000-0000-000000000000') return { required: true }; //fix
    return !control.value && isRequired ? { required: true } : null;
  };
};
export const minDate = (date?: Date, errorMessage?: string): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: unknown } | null => {
    date ??= new Date(new Date().setHours(0, 0, 0, 0));
    if (!control?.value) return null;
    const valueDate = typeof control.value === 'string' ? new Date(control.value) : control.value;

    if (valueDate.getTime() >= date.getTime()) {
      return null;
    } else {
      return errorMessage ? { errorMessage } : { minDate: { actual: valueDate, minDate: date } };
    }
  };
};
export const maxDate = (date?: Date, errorMessage?: string): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: unknown } | null => {
    date ??= new Date(new Date().setHours(0, 0, 0, 0));
    if (!control?.value) return null;
    const valueDate = typeof control.value === 'string' ? new Date(control.value) : control.value;
    if (valueDate.getTime() <= date.getTime()) {
      return null;
    } else {
      return errorMessage ? { errorMessage } : { maxDate: { actual: valueDate, maxDate: date } };
    }
  };
};
/** The controls whose sibling date is being re-checked right now by one of the "than field" validators below. */
const siblingRecheckInProgress = new WeakSet<AbstractControl>();

/**
 * A "than field" date validator found its own date valid: the sibling's error may be stale, so the sibling is re-checked - but only when
 * it shows an error, and never back into the control whose re-check is running. Without that guard a pair of dates checked against each
 * other (Arrival / Departure, Birthdate / Passport issue) with BOTH errors shown re-checked each other without end - each saw the other's
 * old error, since a control stores its errors only after its validators return - and setValue threw RangeError, so both stayed red
 * (website-web#141, R-CIF10).
 */
const recheckSiblingDate = (control: AbstractControl, sibling: AbstractControl): void => {
  if (!sibling.errors?.['minDate'] && !sibling.errors?.['errorMessage']) return;
  if (siblingRecheckInProgress.has(sibling)) return; // the sibling's own re-check is what runs this validator
  siblingRecheckInProgress.add(control);
  try {
    sibling.updateValueAndValidity();
  } finally {
    siblingRecheckInProgress.delete(control);
  }
};
export const dateDifferenceWithField = (
  controllerOrField: AbstractControl | string,
  diffFn: (inputDate: Date, compareDate: Date) => boolean,
  errorMessage: string
): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: unknown } | null => {
    if (!control.value && !controllerOrField) return null;

    let toCompareController: AbstractControl | null;

    if (typeof controllerOrField === 'string') {
      const group = control.parent;
      if (!group) return null;
      toCompareController = group.get(controllerOrField);
    } else {
      toCompareController = controllerOrField;
    }
    if (!toCompareController?.value) return null;

    const date =
      typeof toCompareController.value === 'string' ? new Date(toCompareController.value) : toCompareController.value;

    const valueDate = typeof control.value === 'string' ? new Date(control.value) : control.value;
    if (!diffFn(valueDate, date)) {
      recheckSiblingDate(control, toCompareController);
      return null;
    } else {
      return errorMessage ? { errorMessage } : { dateTiff: { actual: valueDate, dateTiff: date } };
    }
  };
};
export const minDateThanField = (
  controllerOrField: AbstractControl | string,
  errorMessage?: string | null,
  diff?: number
): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: unknown } | null => {
    if (!control?.value && !controllerOrField) return null;

    let toCompareController: AbstractControl | null;

    if (typeof controllerOrField === 'string') {
      const group = control.parent;
      if (!group) return null;
      toCompareController = group.get(controllerOrField);
    } else {
      toCompareController = controllerOrField;
    }
    if (!toCompareController?.value || !control.value) return null;

    const date = addDays(
      typeof toCompareController.value === 'string' ? new Date(toCompareController.value) : toCompareController.value,
      diff ?? 0
    );
    const valueDate = typeof control.value === 'string' ? new Date(control.value) : control.value;
    if (valueDate?.getTime() >= date?.getTime()) {
      recheckSiblingDate(control, toCompareController);
      return null;
    } else {
      return errorMessage ? { errorMessage } : { minDate: { actual: valueDate, minDate: date } };
    }
  };
};
export const maxDateThanField = (
  controllerOrField: AbstractControl | string,
  errorMessage?: string | null,
  diff?: number
): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: unknown } | null => {
    // an empty value is `required`'s business; reading it below threw and froze the control's status (website-web#120, R-CK7a)
    if (!control.value) return null;

    let toCompareController: AbstractControl | null;

    if (typeof controllerOrField === 'string') {
      const group = control.parent;
      if (!group) return null;
      toCompareController = group.get(controllerOrField);
    } else {
      toCompareController = controllerOrField;
    }
    if (!toCompareController?.value) return null;

    const date = addDays(
      typeof toCompareController.value === 'string' ? new Date(toCompareController.value) : toCompareController.value,
      diff ?? 0
    );

    const valueDate = typeof control.value === 'string' ? new Date(control.value) : control.value;
    if (valueDate.getTime() <= date.getTime()) {
      recheckSiblingDate(control, toCompareController);
      return null;
    } else {
      return errorMessage ? { errorMessage } : { maxDate: { actual: valueDate, maxDate: date } };
    }
  };
};
export const justLatinLetters = (): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: unknown } | null => {
    if (!control.value) return null;
    const isLatin = /^[A-Za-z 0-9!@#$%^&*(),.?"';:{}|<>+=_-]+$/.test(control.value);
    return !isLatin ? { justLetters: { actual: control.value } } : null;
  };
};

/**
 * website-web#125: the flight time box is masked `00:00`, so a stored `9:30` would render as `93:0`. The server accepts `h:mm` and `hh:mm:ss`:
 * `9:30` -> `09:30`, `09:30:00` -> `09:30`; anything else (a half-typed value, null) is returned unchanged.
 */
export const normalizeFlightTime = <T>(value: T): T | string => {
  if (typeof value !== 'string') return value;
  if (/^\d:\d\d$/.test(value)) return `0${value}`;
  if (/^\d\d:\d\d:\d\d$/.test(value)) return value.slice(0, 5);
  return value;
};

export const timeMaskFormat = (): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: unknown } | null => {
    if (!control.value) return null;
    const isCorrectFormat = /^([01]?\d|2[0-3]):[0-5]\d$/.test(control.value);
    return !isCorrectFormat ? { timeMask: { actual: control.value } } : null;
  };
};

/** docs#213 (R-WB22a): a tourist's First / Last Name holds only Latin letters, space, dot, apostrophe and hyphen (up to 150 characters, `maxLengthWithMessage`). */
export const TOURIST_NAME_PATTERN = /^[A-Za-z .'-]+$/;

/**
 * website-web#123 (Amiran 24.10.2026, checkout.md R-CK30): a tourist's First / Last Name holds at least one Latin letter - ".", "-", "'" alone is
 * refused. Listed BEFORE `TOURIST_NAME_PATTERN` in a control's validators: both report `pattern`, the later one wins, so a name with other
 * characters keeps the "Only Latin letters ..." reason and only a letter-less name shows this one.
 */
export const TOURIST_NAME_LETTER_PATTERN = /[A-Za-z]/;
export const TOURIST_NAME_LETTER_MESSAGE = 'A name must contain at least one Latin letter';

/** website-web#124 (R-CK9): the Azerbaijani FIN code holds Latin letters and digits only (its length of 7 is `requiredLength`). */
export const FIN_PATTERN = /^[A-Za-z0-9]+$/;
export const FIN_MESSAGE = 'Only Latin letters and digits are allowed';

export const regExpPattern = (pattern: RegExp, customMessage?: string): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: unknown } | null => {
    if (!control.value) return null;
    const valid = pattern.test(control.value);
    return !valid ? { pattern: { actual: control.value, pattern, customMessage } } : null;
  };
};

export const justNumbers = (): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: unknown } | null => {
    if (!control.value) return null;
    const isNumber = /^\d+$/.test(control.value);
    return !isNumber ? { justNumbers: { actual: control.value } } : null;
  };
};

export const noJustWhitespace = (): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: unknown } | null => {
    if (!control.value) return null;
    return (control.value ?? '').trim().length ? null : { whitespace: true };
  };
};
export const noWhiteSpaceEndOfField = (): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: unknown } | null => {
    const endSpace = /\s$/;
    if (!control.value) return null;
    return !endSpace.test(control.value) ? null : { whitespaceEnd: true };
  };
};
export const minNumbers = (minNumbers: number, customMessage?: string): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: unknown } | null => {
    if (typeof control.value === 'number' && control.value >= minNumbers) return null;
    return { minNumbers: { minNumbers, actual: control.value ?? 0, customMessage } };
  };
};

export const maxTextLengthValidator = (max: number): ValidatorFn => {
  return (control: AbstractControl) => {
    const html = control.value as string | null;
    if (!html) return null;
    const text = htmlToText(html);
    if (text.length > max) return { maxlength: { requiredLength: max, actualLength: text.length } };
    return null;
  };
};

/**
 * `Validators.maxLength` reported through `errorMessage`, so the form shows its own wording instead
 * of the generic "Maximum length of field must be…" one. Pass `{ text, data }` for a translatable
 * template: `text` is the key, `data` its interpolation params.
 */
export const maxLengthWithMessage = (
  max: number,
  errorMessage: NonNullable<ValidationErrorValues['errorMessage']>
): ValidatorFn => {
  const maxLength = Validators.maxLength(max);
  return (control: AbstractControl): ValidationErrors | null => (maxLength(control) ? { errorMessage } : null);
};
